import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { X, Send, Ticket as TicketIcon } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { formatDateTime, formatDate } from "@/components/admin/ui";
import { TICKET_STATUS_META, starRow } from "./supportMeta";

const TABS = ["Tickets", "Jobs", "Invoices", "Leads", "Communication"];

export const CustomerContextPanel = ({ API, customerId, onClose, onNewTicket, onSelectTicket }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("Tickets");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/org/support/customers/${customerId}/context`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, customerId]);

  useEffect(() => { load(); }, [load]);

  const availableTabs = TABS.filter((t) => {
    if (t === "Tickets" || t === "Communication") return true;
    if (t === "Jobs") return !!data?.jobs;
    if (t === "Invoices") return !!data?.invoices;
    if (t === "Leads") return !!data?.leads;
    return false;
  });
  const activeTab = availableTabs.includes(tab) ? tab : "Tickets";

  const sendMessage = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/org/support/communications`, { customer_id: customerId, channel: "portal", body: message.trim() }, { withCredentials: true });
      setMessage("");
      load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSending(false); }
  };

  if (error) return <div className="rounded-2xl border border-red-200 bg-white p-4 text-sm font-semibold text-red-600 dark:border-red-500/30 dark:bg-[#111C33]" data-testid="support-context-error">{error}</div>;
  if (!data) return <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-400 dark:border-white/10 dark:bg-[#111C33]" data-testid="support-context-loading">Loading…</div>;

  const c = data.customer;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-[#111C33]" data-testid="support-customer-context-panel">
      <div className="flex items-start justify-between border-b border-slate-200 p-4 dark:border-white/10">
        <div>
          <p className="text-base font-extrabold text-slate-950 dark:text-white" data-testid="support-context-name">{c.first_name} {c.last_name}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{c.email || "—"} · {c.phone || "—"}</p>
        </div>
        <button type="button" onClick={onClose} data-testid="support-context-close" className="text-slate-400 hover:text-slate-700 dark:hover:text-white"><X size={16} /></button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 p-3 dark:border-white/10">
        <button type="button" onClick={() => onNewTicket(c)} data-testid="support-context-action-new-ticket" className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">
          <TicketIcon size={12} /> New Ticket
        </button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200 px-3 dark:border-white/10">
        {availableTabs.map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} data-testid={`support-context-tab-${t.toLowerCase()}`}
            className={`border-b-2 px-2.5 py-2 text-xs font-bold transition-colors duration-150 ${activeTab === t ? "border-[#0052FF] text-[#0052FF]" : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="max-h-[420px] overflow-y-auto p-4">
        {activeTab === "Tickets" && (
          data.tickets.length === 0 ? <p className="text-sm text-slate-400" data-testid="support-context-tickets-empty">No tickets for this customer.</p> : (
            <div className="space-y-2" data-testid="support-context-tickets">
              {data.tickets.map((t) => (
                <button key={t.ticket_id} type="button" onClick={() => onSelectTicket(t)} data-testid={`support-context-ticket-${t.ticket_id}`} className="block w-full rounded-lg border border-slate-200 p-2.5 text-left text-sm hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5">
                  <p className="font-semibold text-slate-900 dark:text-white">{t.subject}</p>
                  <div className="mt-1 flex items-center justify-between text-xs text-slate-400">
                    <span>{t.display_id}</span>
                    <span className={`rounded-full border px-2 py-0.5 font-bold ${TICKET_STATUS_META[t.status].cls}`}>{TICKET_STATUS_META[t.status].label}</span>
                  </div>
                </button>
              ))}
            </div>
          )
        )}
        {activeTab === "Jobs" && (
          (!data.jobs || data.jobs.length === 0) ? <p className="text-sm text-slate-400" data-testid="support-context-jobs-empty">No jobs on file.</p> : (
            <div className="space-y-2" data-testid="support-context-jobs">
              {data.jobs.map((j) => (
                <div key={j.job_id} className="rounded-lg border border-slate-200 p-2.5 text-sm dark:border-white/10">
                  <p className="font-semibold text-slate-900 dark:text-white">{j.title}</p>
                  <p className="text-xs text-slate-400">{j.status} · {formatDateTime(j.scheduled_start)}</p>
                </div>
              ))}
            </div>
          )
        )}
        {activeTab === "Invoices" && (
          (!data.invoices || data.invoices.length === 0) ? <p className="text-sm text-slate-400" data-testid="support-context-invoices-empty">No invoices on file.</p> : (
            <div className="space-y-2" data-testid="support-context-invoices">
              {data.invoices.map((i) => (
                <div key={i.invoice_id} className="flex items-center justify-between rounded-lg border border-slate-200 p-2.5 text-sm dark:border-white/10">
                  <span className="font-semibold text-[#0052FF]">{i.display_id}</span>
                  <span className="text-xs text-slate-400">{i.status} · Balance ${(i.balance_cents / 100).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )
        )}
        {activeTab === "Leads" && (
          (!data.leads || data.leads.length === 0) ? <p className="text-sm text-slate-400" data-testid="support-context-leads-empty">No leads on file.</p> : (
            <div className="space-y-2" data-testid="support-context-leads">
              {data.leads.map((l) => (
                <div key={l.lead_id} className="flex items-center justify-between rounded-lg border border-slate-200 p-2.5 text-sm dark:border-white/10">
                  <span className="font-semibold text-[#0052FF]">{l.display_id}</span>
                  <span className="text-xs text-slate-400 capitalize">{l.status}</span>
                </div>
              ))}
            </div>
          )
        )}
        {activeTab === "Communication" && (
          <div className="space-y-3" data-testid="support-context-communication">
            {data.communications.length === 0 ? <p className="text-sm text-slate-400">No messages yet.</p> : [...data.communications].reverse().map((m) => (
              <div key={m.communication_id} className={`rounded-lg border p-2.5 text-sm ${m.direction === "inbound" ? "border-slate-200 dark:border-white/10" : "border-blue-200 bg-blue-50 dark:border-blue-500/20 dark:bg-blue-500/10"}`}>
                <p className="text-slate-900 dark:text-white">{m.body}</p>
                <p className="mt-1 text-xs text-slate-400">{m.direction === "inbound" ? "Customer" : "Staff"} · {m.channel} · {formatDateTime(m.created_at)}</p>
              </div>
            ))}
            <div className="flex items-start gap-2 pt-1">
              <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Reply to customer (visible in their Portal)…" data-testid="support-context-message-input" className="flex-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
              <button type="button" onClick={sendMessage} disabled={sending || !message.trim()} data-testid="support-context-send-message" className="shrink-0 rounded-lg border border-transparent bg-[#0052FF] p-2 text-white disabled:opacity-40"><Send size={14} /></button>
            </div>
          </div>
        )}
      </div>

      {data.feedback.length > 0 && (
        <div className="border-t border-slate-200 p-3 text-xs text-slate-500 dark:border-white/10 dark:text-slate-400" data-testid="support-context-feedback">
          Last feedback: <span className="font-bold text-amber-500">{starRow(data.feedback[0].rating)}</span> {data.feedback[0].comment ? `"${data.feedback[0].comment}"` : ""}
        </div>
      )}
    </div>
  );
};
